import { every, filter, includes, isEmpty, some, trimStart } from 'lodash'
import { computed, type Ref } from 'vue'
import type { NavItemBase } from '@/types/nav'
import ENV from '@/constants/env'
import type { TithelyNavSettingsItem } from '@/types/nav/tithelyNav'
import type { useMe } from '@/queries/me'

const filterNavPermissions = <NavItem extends NavItemBase | TithelyNavSettingsItem>(
    items: NavItem[],
    { data: me, hasGiving, hasTithelyUi, hasTithelyCustomerId }: ReturnType<typeof useMe>
): NavItem[] =>
    filter(items, (item: NavItem) => {
        const features = computed(() => me.value?.features ?? [])
        const roles = computed(() => [...(me.value?.roles ?? []), ...(me.value?.isPrimary ? [`primary`] : [])])

        // Invalid item
        if (!item.route && !item.action && !item.sso) {
            return false
        }

        const localBypass = ENV.environment === `local` || includes(features.value, 'dev-nav-override')

        // Tithely Giving check
        if (item.isTithelyGivingEnabled !== undefined && item.isTithelyGivingEnabled !== hasGiving.value) {
            return false
        }

        // Tithely 3.0 check
        if (item.hasTithelyUi !== undefined && item.hasTithelyUi !== hasTithelyUi.value) {
            return false
        }

        // Does customer_id === `tithely`? (for Groups access, 2.0 and above)
        if ('hasTithelyCustomerId' in item && item.hasTithelyCustomerId !== hasTithelyCustomerId.value) {
            return false
        }

        // Finally check the roles
        return (
            checkFeatures(features, localBypass ? item.localFeatures ?? item.features : item.features) &&
            checkRoles(roles, item.roles, item.orRoles)
        )
    })

const checkFeatures = (existingFeatures: Ref<string[]>, desiredFeatures: string[] = []): boolean => {
    if (isEmpty(desiredFeatures)) {
        return true
    }

    const negativeFeatures = filter(desiredFeatures, feature => feature[0] === `!`)
    if (some(negativeFeatures, feature => includes(existingFeatures.value, trimStart(feature, `!`)))) {
        return false
    }

    const positiveFeatures = filter(desiredFeatures, feature => feature[0] !== `!`)
    return every(positiveFeatures, feature => includes(existingFeatures.value, feature))
}

const checkRoles = (existingRoles: Ref<string[]>, desiredRoles: string[] = [], orRoles = false): boolean => {
    if (isEmpty(desiredRoles)) {
        return true
    }

    const negativeRoles = filter(desiredRoles, role => role[0] === `!`)
    if (some(negativeRoles, role => includes(existingRoles.value, trimStart(role, `!`)))) {
        return false
    }

    const positiveRoles = filter(desiredRoles, role => role[0] !== `!`)
    const roleCheck = orRoles ? some : every
    return roleCheck(positiveRoles ?? [], role => includes(existingRoles.value, role))
}

export { filterNavPermissions }
